import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';

/**
 * A real combobox in place of the native `<select>` the scenario picker used
 * to be. The open popup of a native select is OS-painted and cannot be
 * styled at all: that is the entire cause of the white scrollbar on an
 * otherwise dark instrument, and no amount of styling the closed control
 * reaches it. Built to the ARIA 1.2 combobox pattern: focus stays on the
 * trigger the whole time, the active option is communicated with
 * `aria-activedescendant`, and the popup itself is a plain `role="listbox"`.
 *
 * The accessible name is wired through `labelId` rather than an internal
 * label, so the same visible `<label>` element the native select used keeps
 * naming this control; `getByLabel('Scenario')` keeps resolving to it.
 */

export interface ScenarioOption {
  id: string;
  proves: string;
}

function ChevronIcon(): ReactElement {
  return (
    <svg
      className="scenario-select__chevron"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

const TYPEAHEAD_RESET_MS = 600;

export function ScenarioSelect({
  labelId,
  value,
  options,
  disabled = false,
  onChange,
}: {
  labelId: string;
  value: string;
  options: readonly ScenarioOption[];
  disabled?: boolean;
  onChange: (id: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const typeaheadRef = useRef({ buffer: '', at: 0 });
  const listboxId = useId();

  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.id === value),
  );

  const close = (refocus: boolean): void => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const openAt = (index: number): void => {
    setActiveIndex(Math.max(0, Math.min(options.length - 1, index)));
    setOpen(true);
  };

  const commit = (index: number): void => {
    const option = options[index];
    if (option === undefined) return;
    onChange(option.id);
    close(true);
  };

  // Flip upward when the popup would otherwise run past the viewport's
  // bottom edge, measured against its own height once it has mounted rather
  // than assumed from a fixed figure.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const popup = popupRef.current;
    if (trigger === null || popup === null) return;
    const triggerRect = trigger.getBoundingClientRect();
    const room = window.innerHeight - triggerRect.bottom;
    setPlacement(room < popup.offsetHeight + 24 ? 'top' : 'bottom');
  }, [open]);

  // Outside pointer down closes without stealing focus back.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) === true) return;
      if (popupRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const typeahead = (char: string): void => {
    const now = Date.now();
    const state = typeaheadRef.current;
    state.buffer = now - state.at > TYPEAHEAD_RESET_MS ? char : state.buffer + char;
    state.at = now;
    const needle = state.buffer.toLowerCase();
    const match = options.findIndex((option) => option.id.toLowerCase().startsWith(needle));
    if (match >= 0) {
      if (open) setActiveIndex(match);
      else openAt(match);
    }
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (open) setActiveIndex((i) => Math.min(options.length - 1, i + 1));
        else openAt(selectedIndex);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (open) setActiveIndex((i) => Math.max(0, i - 1));
        else openAt(selectedIndex);
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) commit(activeIndex);
        else openAt(selectedIndex);
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          close(true);
        }
        break;
      case 'Tab':
        if (open) close(false);
        break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          typeahead(event.key);
        }
        break;
    }
  };

  const activeOption = options[activeIndex];

  return (
    <div className="scenario-select">
      <button
        ref={triggerRef}
        type="button"
        className="scenario-select__trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-labelledby={labelId}
        aria-activedescendant={open && activeOption ? `${listboxId}-${activeOption.id}` : undefined}
        disabled={disabled}
        onClick={() => {
          if (open) close(true);
          else openAt(selectedIndex);
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="mono scenario-select__value">{value}</span>
        <ChevronIcon />
      </button>

      {open && (
        <>
          <button
            type="button"
            className="scenario-select__scrim"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => {
              close(true);
            }}
          />
          <div
            ref={popupRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={labelId}
            className="scenario-select__popup"
            data-placement={placement}
          >
            {options.map((option, index) => (
              <div
                key={option.id}
                id={`${listboxId}-${option.id}`}
                role="option"
                aria-selected={option.id === value}
                data-active={index === activeIndex}
                className="scenario-select__option"
                onPointerEnter={() => {
                  setActiveIndex(index);
                }}
                onClick={() => {
                  commit(index);
                }}
              >
                <span className="mono scenario-select__option-id">{option.id}</span>
                <span className="muted scenario-select__option-proves">{option.proves}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
